/**
 * Reminders Store — locally-persisted reminders synced with server.
 *
 * Sync strategy (local-first):
 * - On WS reconnect: full sync from server
 * - On server 'sync' event: full sync (server tells us data changed)
 * - On reminder_fired event: full sync
 * - On pull-to-refresh: full sync
 * - On mutations (pause/done/snooze/etc): optimistic local update + server call
 * - Tab focus: NO sync — use local data
 * - WS messages: NO auto-sync — wait for explicit sync event
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Alert } from 'react-native';
import type { Reminder } from '../types/api';
import { getReminderStatus } from '../types/api';
import * as api from '../api/client';
import { wsManager } from '../ws/WebSocketManager';
import { rescheduleNotifications, updateBadgeCount } from '../utils/notifications';
import { useAuthStore } from './authStore';
import { useOfflineQueue } from './offlineQueueStore';
import { requestOfflineQueueReplay } from './offlineQueueReplay';
import i18n from '../i18n';

type ReminderFilter = 'active' | 'today' | 'week' | 'history';

interface FiredReminder {
  seq: number;
  body: string;
  firedAt: string;
  buttons: { id: string; title: string }[];
}

interface RemindersState {
  reminders: Reminder[];
  isLoading: boolean;
  isSyncing: boolean;
  filter: ReminderFilter;
  searchQuery: string;
  firedReminder: FiredReminder | null;
  lastSyncedAt: string | null;

  // Actions
  setFilter: (filter: ReminderFilter) => void;
  setSearchQuery: (query: string) => void;
  loadReminders: () => Promise<void>;
  syncFromServer: (force?: boolean) => Promise<void>;
  rescheduleFromLocal: () => void;
  snooze: (seq: number, minutes?: number, options?: {
    occurrenceRunAt?: string | null;
    requireOccurrenceIdentity?: boolean;
  }) => Promise<void>;
  pause: (seq: number) => Promise<void>;
  resume: (seq: number) => Promise<void>;
  skip: (seq: number) => Promise<void>;
  markDone: (seq: number, options?: {
    suppressUndoPrompt?: boolean;
    occurrenceRunAt?: string | null;
    requireOccurrenceIdentity?: boolean;
  }) => Promise<void>;
  undoDone: (seq: number) => Promise<{ isOverdue: boolean }>;
  deleteReminder: (seq: number, opts?: { googleAction?: 'delete' | 'keep' }) => Promise<void>;
  syncReminderToGoogle: (seq: number) => Promise<void>;
  unsyncReminderFromGoogle: (seq: number) => Promise<void>;
  deleteAll: () => Promise<void>;
  dismissFired: () => void;
  initSync: () => void;
  cleanupSync: () => void;
}

export const useRemindersStore = create<RemindersState>()(
  persist(
    (set, get) => {
      let _unsubSync: (() => void) | null = null;
      let _unsubFired: (() => void) | null = null;
      let _unsubState: (() => void) | null = null;
      let _syncInFlight: Promise<void> | null = null;

      /** Get headsup preferences from the current user */
      function getHeadsupOpts() {
        const user = useAuthStore.getState().user;
        return {
          headsupEnabled: user?.headsupEnabled ?? true,
          headsupMinutes: user?.headsupMinutes ?? 5,
        };
      }

      /** Replace reminders and reschedule local notifications */
      function setReminders(reminders: Reminder[]) {
        set({ reminders });
        rescheduleNotifications(reminders, getHeadsupOpts());
        // Update app icon badge with overdue count
        const now = new Date();
        const overdueCount = reminders.filter(r =>
          getReminderStatus(r) === 'active' && r.run_at_utc && new Date(r.run_at_utc) < now
        ).length;
        updateBadgeCount(overdueCount);
      }

      /** Optimistic update: patch a single reminder locally */
      function patchLocal(seq: number, patch: Partial<Reminder>) {
        const updated = get().reminders.map((r) =>
          r.user_seq === seq ? { ...r, ...patch } : r
        );
        setReminders(updated);
      }

      /** Revert to snapshot on error */
      function revertAndAlert(snapshot: Reminder[], msg: string) {
        setReminders(snapshot);
        Alert.alert(i18n.t('auth.error'), msg);
      }

      /** Check if error is a network error; if so, queue for offline replay */
      function isNetworkError(err: unknown): boolean {
        const e = err as { code?: string; message?: string; response?: unknown };
        if (e?.response) return false;
        return ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(e?.code || '')
          || /network error|timed?\s*out/i.test(e?.message || '');
      }

      function queueOffline(method: 'post' | 'put' | 'delete', url: string, data?: unknown): boolean {
        const ownerId = useAuthStore.getState().user?.waId?.trim();
        if (!ownerId) {
          console.warn('[OfflineQueue] Cannot queue mutation without an authenticated owner');
          return false;
        }
        useOfflineQueue.getState().enqueue(ownerId, method, url, data);
        requestOfflineQueueReplay(0);
        return true;
      }

      return {
        reminders: [],
        isLoading: false,
        isSyncing: false,
        filter: 'active',
        searchQuery: '',
        firedReminder: null,
        lastSyncedAt: null,

        setFilter: (filter) => {
          set({ filter });
        },

        setSearchQuery: (query) => set({ searchQuery: query }),

        loadReminders: async () => {
          set({ isLoading: true });
          try {
            const { reminders } = await api.getReminders();
            setReminders(reminders);
            set({ isLoading: false, lastSyncedAt: new Date().toISOString() });
          } catch (err) {
            console.warn('[Reminders] loadReminders failed:', err);
            set({ isLoading: false });
          }
        },

        syncFromServer: async (force = false) => {
          if (_syncInFlight) {
            if (!force) return _syncInFlight;
            // A mutation must not coalesce into a request that started before
            // that mutation. Let it finish, then fetch the post-mutation state.
            await _syncInFlight;
          }

          set({ isSyncing: true });
          _syncInFlight = (async () => {
            try {
              const { reminders } = await api.getReminders();
              setReminders(reminders);
              set({ lastSyncedAt: new Date().toISOString() });
            } catch (err) {
              console.warn('[Reminders] syncFromServer failed:', err);
              rescheduleNotifications(get().reminders, getHeadsupOpts());
            } finally {
              set({ isSyncing: false });
              _syncInFlight = null;
            }
          })();

          return _syncInFlight;
        },

        rescheduleFromLocal: () => {
          rescheduleNotifications(get().reminders, getHeadsupOpts());
        },

        snooze: async (seq, minutes = 15, options) => {
          const snapshot = get().reminders;
          const cachedReminder = snapshot.find((reminder) => reminder.user_seq === seq);
          const identityWasProvided = !!options
            && Object.prototype.hasOwnProperty.call(options, 'occurrenceRunAt');
          const occurrenceRunAt = identityWasProvided
            ? (options?.occurrenceRunAt || null)
            : (cachedReminder?.run_at_utc || null);

          if (options?.requireOccurrenceIdentity && !occurrenceRunAt) {
            set({ firedReminder: null });
            await get().syncFromServer(true);
            return;
          }

          // A notification may refer to an occurrence the server has already
          // advanced. Avoid locally moving/scheduling the cached next occurrence
          // until the identity-aware server response confirms the snooze.
          const optimisticallyUpdate = !options?.requireOccurrenceIdentity && !cachedReminder?.is_recurring;
          if (optimisticallyUpdate) {
            patchLocal(seq, {
              run_at_utc: new Date(Date.now() + minutes * 60000).toISOString(),
            });
          }
          try {
            const result = await api.snoozeReminder(seq, minutes, occurrenceRunAt);
            const returnedReminders = [result.reminder, result.snoozedReminder].filter(Boolean);
            if (returnedReminders.length > 0) {
              let updated = [...get().reminders];
              for (const returned of returnedReminders) {
                const index = updated.findIndex(item => item.user_seq === returned.user_seq);
                const normalized = {
                  ...(index >= 0 ? updated[index] : {}),
                  ...returned,
                  classification: undefined,
                  next_run_utc: null,
                } as Reminder;
                if (index >= 0) updated[index] = normalized;
                else updated.push(normalized);
              }
              setReminders(updated);
            }
            await get().syncFromServer(true);
          } catch (err) {
            if (isNetworkError(err)) {
              const queued = queueOffline('post', `/api/v1/reminders/${seq}/snooze`, {
                minutes,
                occurrenceRunAt: occurrenceRunAt || undefined,
              });
              if (!queued) revertAndAlert(snapshot, i18n.t('reminder.snoozeFailed'));
            } else {
              revertAndAlert(snapshot, i18n.t('reminder.snoozeFailed'));
            }
          }
        },

        pause: async (seq) => {
          const snapshot = get().reminders;
          patchLocal(seq, { is_paused: 1 });
          try {
            await api.pauseReminder(seq);
            await get().syncFromServer();
          } catch (err) {
            if (isNetworkError(err)) {
              if (!queueOffline('post', `/api/v1/reminders/${seq}/pause`)) {
                revertAndAlert(snapshot, i18n.t('reminder.pauseFailed'));
              }
            } else {
              revertAndAlert(snapshot, i18n.t('reminder.pauseFailed'));
            }
          }
        },

        resume: async (seq) => {
          const snapshot = get().reminders;
          patchLocal(seq, { is_paused: 0 });
          try {
            await api.resumeReminder(seq);
            await get().syncFromServer();
          } catch (err) {
            if (isNetworkError(err)) {
              if (!queueOffline('post', `/api/v1/reminders/${seq}/resume`)) {
                revertAndAlert(snapshot, i18n.t('reminder.resumeFailed'));
              }
            } else {
              revertAndAlert(snapshot, i18n.t('reminder.resumeFailed'));
            }
          }
        },

        skip: async (seq) => {
          const snapshot = get().reminders;
          try {
            const result = await api.skipReminder(seq);
            if (result.reminder) {
              const updated = get().reminders.map((reminder) =>
                reminder.user_seq === seq ? { ...reminder, ...result.reminder } : reminder
              );
              setReminders(updated);
            }
            await get().syncFromServer();
          } catch (err) {
            if (isNetworkError(err)) {
              if (!queueOffline('post', `/api/v1/reminders/${seq}/skip`)) {
                revertAndAlert(snapshot, i18n.t('reminder.skipFailed'));
              }
            } else {
              revertAndAlert(snapshot, i18n.t('reminder.skipFailed'));
            }
          }
        },

        markDone: async (seq, options) => {
          const snapshot = get().reminders;
          const cachedReminder = snapshot.find((reminder) => reminder.user_seq === seq);
          const identityWasProvided = !!options
            && Object.prototype.hasOwnProperty.call(options, 'occurrenceRunAt');
          const occurrenceRunAt = identityWasProvided
            ? (options?.occurrenceRunAt || null)
            : (cachedReminder?.run_at_utc || null);

          // Pre-OTA notifications do not identify the occurrence that created
          // them. Never substitute the current recurring row: it may already
          // be the next occurrence and would be skipped by a legacy tap.
          if (options?.requireOccurrenceIdentity && !occurrenceRunAt) {
            set({ firedReminder: null });
            await get().syncFromServer(true);
            return;
          }
          try {
            const result = await api.markReminderDone(seq, occurrenceRunAt);
            if (result.reminder) {
              patchLocal(seq, {
                ...result.reminder,
                classification: undefined,
                next_run_utc: null,
              });
            }
            await get().syncFromServer(true);
            if (result.undoAvailable && !options?.suppressUndoPrompt) {
              Alert.alert(
                i18n.t('reminder.done'),
                i18n.t('reminder.undoPrompt'),
                [
                  { text: i18n.t('common.close'), style: 'cancel' },
                  { text: i18n.t('reminder.undo'), onPress: () => { void get().undoDone(seq); } },
                ],
              );
            }
          } catch (err) {
            if (isNetworkError(err)) {
              const queued = queueOffline('post', `/api/v1/reminders/${seq}/done`, {
                occurrenceRunAt: occurrenceRunAt || undefined,
              });
              if (queued) set({ firedReminder: null });
              else revertAndAlert(snapshot, i18n.t('reminder.doneFailed'));
            } else {
              revertAndAlert(snapshot, i18n.t('reminder.doneFailed'));
            }
          }
        },

        undoDone: async (seq) => {
          const snapshot = get().reminders;
          try {
            const result = await api.undoReminderDone(seq);
            await get().syncFromServer();
            return { isOverdue: !!result.isOverdue };
          } catch {
            revertAndAlert(snapshot, i18n.t('reminder.undoFailed'));
            return { isOverdue: false };
          }
        },

        deleteReminder: async (seq, opts) => {
          const snapshot = get().reminders;
          const updated = get().reminders.filter((r) => r.user_seq !== seq);
          setReminders(updated);
          try {
            // opts.googleAction='delete' also removes the synced Google event.
            await api.deleteReminder(seq, opts);
          } catch (err) {
            if (isNetworkError(err)) {
              const qs = opts?.googleAction === 'delete' ? '?google=delete' : '';
              if (!queueOffline('delete', `/api/v1/reminders/${seq}${qs}`)) {
                revertAndAlert(snapshot, i18n.t('reminder.deleteFailed'));
              }
            } else {
              revertAndAlert(snapshot, i18n.t('reminder.deleteFailed'));
            }
          }
        },

        syncReminderToGoogle: async (seq) => {
          try { await api.syncReminderToGoogle(seq); await get().syncFromServer?.(); } catch { /* surfaced by caller */ }
        },
        unsyncReminderFromGoogle: async (seq) => {
          try { await api.unsyncReminderFromGoogle(seq); await get().syncFromServer?.(); } catch { /* surfaced by caller */ }
        },

        deleteAll: async () => {
          const snapshot = get().reminders;
          setReminders([]);
          try {
            await api.deleteAllReminders();
          } catch {
            revertAndAlert(snapshot, i18n.t('reminders.deleteAllFailed'));
          }
        },

        dismissFired: () => set({ firedReminder: null }),

        initSync: () => {
          // Listen for server-side sync events (server tells us data changed)
          _unsubSync = wsManager.on('sync', (data) => {
            const { entity } = data.payload || {};
            if (entity === 'reminder') {
              get().syncFromServer();
            }
            if (entity === 'preferences') {
              // Preferences changed on another device — refresh profile + reschedule
              useAuthStore.getState().refreshProfile();
              get().rescheduleFromLocal();
            }
          });

          // Listen for reminder_fired events
          _unsubFired = wsManager.on('reminder_fired', (data) => {
            const { seq, body, firedAt, buttons } = data.payload || {};
            if (seq) {
              set({ firedReminder: { seq, body, firedAt, buttons: buttons || [] } });
              get().syncFromServer();
            }
          });

          // Sync once when WS reconnects; schedule local notifications when WS disconnects
          // Fallback: poll every 30s when WS is not connected
          let pollInterval: ReturnType<typeof setInterval> | null = null;
          _unsubState = wsManager.onStateChange((wsState) => {
            if (wsState === 'connected') {
              get().syncFromServer();
              // Also refresh profile to pick up any missed preference changes (e.g. tagColors)
              useAuthStore.getState().refreshProfile();
              // Stop polling when WS reconnects
              if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }
            } else if (wsState === 'disconnected') {
              // WS went down — schedule local notifications as offline fallback
              rescheduleNotifications(get().reminders, getHeadsupOpts());
              // Start polling fallback
              if (!pollInterval) {
                pollInterval = setInterval(() => { get().syncFromServer(); }, 30_000);
              }
            }
          });
        },

        cleanupSync: () => {
          _unsubSync?.();
          _unsubSync = null;
          _unsubFired?.();
          _unsubFired = null;
          _unsubState?.();
          _unsubState = null;
        },
      };
    },
    {
      name: 'fm-reminders',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({
        reminders: state.reminders,
        lastSyncedAt: state.lastSyncedAt,
      }),
    },
  ),
);
